import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { FormField, Label, Select, type SelectProps } from "@mdotm/shadcn/mdotm/components";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import equal from "fast-deep-equal";
import type { Control, FieldValues, FormState, Path } from "react-hook-form";

export type SelectFieldProps<T extends FieldValues, TOptions> = StylableProps & {
	control: Control<T, any>;
	formState: FormState<T>;
	name: Path<T>;
	label: NodeOrFn<{ htmlFor: string }>;
	unselectOnMatch?: boolean;
	hideBanner?: boolean;
} & Omit<SelectProps<TOptions>, "value">;

export function SelectField<T extends FieldValues, TOptions>({
	label,
	formState,
	name,
	control,
	className,
	style,
	unselectOnMatch,
	hideBanner = false,
	onChange,
	...forward
}: SelectFieldProps<T, TOptions>): React.JSX.Element {
	return (
		<FormField
			className={className}
			style={style}
			label={({ htmlFor }) =>
				typeof label === "string" ? (
					<Label htmlFor={htmlFor} className="mb-px">
						{label}
					</Label>
				) : (
					renderNodeOrFn(label, { htmlFor })
				)
			}
			error={hideBanner ? undefined : (valueByPath(formState.errors, name) as { message?: string })?.message}
		>
			{(fieldProps) => (
				<FormController
					control={control}
					name={name}
					render={({ field: { ref, ...controllerProps } }) => (
						<Select
							innerRef={ref}
							{...fieldProps}
							{...controllerProps}
							onChange={(e) => {
								onChange?.(e as any);
								if (unselectOnMatch && equal(controllerProps.value, e)) {
									return controllerProps.onChange(null);
								}
								return controllerProps.onChange(e);
							}}
							{...(forward as any)}
						/>
					)}
				/>
			)}
		</FormField>
	);
}
